import {
  DEFAULT_AXIS_FONT_SIZE,
  DEFAULT_AXIS_LABEL_FONT_SIZE,
  DEFAULT_FONT_WEIGHT
} from "constants/chart.constants";
import { RigFracGroupBy } from "constants/charts.enums";
import { XAXisOption, YAXisOption } from "echarts/types/dist/shared";
import { resolveRigFracMetrics } from "entities/charts/eva/requests/rigFracMetrics";
import { createEntityState } from "entities/charts/factory";
import { ChartOption } from "entities/charts/options";
import { EvaChartStates } from "types/factory";
import { isOthersGroupLabel } from "utils/groupBy/isOthersGroupLabel";

import { getRigFracGroupByLabel } from "components/charts/rig-frac/groupByOptions";

import { AXIS_LABEL_COLOR, DEFAULT_Z_INDEX } from "./constants";
import {
  integerValueAxisOptions,
  resolveRigFracSeriesColor,
  rigFracTooltip
} from "./rigFracActivity";
import {
  buildRecentActivityItemTooltipFormatter,
  rigFracMetricLabel
} from "./rigFracTooltip";

// The category-axis label band ADAPTS to the group labels so short names (basins
// like "Permian") don't strand a wide empty gap. It sizes to the BULK of the
// labels — the 75th-percentile length, NOT the single longest — so one unusually
// long name (e.g. "Denver-Julesburg" among short basins) truncates rather than
// stretching the band and pushing every other row's whitespace out. Estimated
// char width ≈ 0.55·fontSize (mean) + a small margin, clamped: MAX so even the
// typical name can't sprawl, MIN so a very short set still reserves a readable
// band. grid.left = width + 32 and nameGap = width + 16 keep the original fixed
// 76 / 92 / 108 ratios. Exported so RigFracMetricSelector can derive the bottom
// button's clamp bounds from the same width range (no drifting magic numbers).
export const RECENT_LABEL_MIN_WIDTH = 56;
export const RECENT_LABEL_MAX_WIDTH = 150;
// Fraction of labels that fit without truncation; the longer tail is clipped.
// Lower → tighter band, more names cut; higher → wider band, fewer cut. NOTE: for
// small sets the percentile can't sit below the longest — with ≤ 3 labels the
// nearest rank IS the longest, so the "size to the majority" property only kicks
// in past that (e.g. a single-group Country grouping sizes to its one label).
const RECENT_LABEL_FIT_PERCENTILE = 0.75;
// minWidth/maxWidth default to this widget's own bounds so every existing caller
// is unaffected; a full-size chart with a roomier left gutter (e.g. Horizontal
// Stacked Bar) passes wider bounds instead of duplicating the percentile logic.
export const recentCategoryLabelWidth = (
  labels: string[],
  fontSize: number,
  minWidth: number = RECENT_LABEL_MIN_WIDTH,
  maxWidth: number = RECENT_LABEL_MAX_WIDTH
): number => {
  // String() (not `?? ""`) so a non-string label from the loosely-typed response
  // yields its stringified length, never NaN — a NaN width would flow into
  // axisLabel.width / nameGap / grid.left and silently break the whole layout.
  const lengths = labels.map((l) => String(l ?? "").length).sort((a, b) => a - b);
  if (lengths.length === 0) return minWidth;
  // Nearest-rank percentile: the shortest length at or under which
  // RECENT_LABEL_FIT_PERCENTILE of the labels fall (the longer tail truncates).
  const rank = Math.ceil(RECENT_LABEL_FIT_PERCENTILE * lengths.length);
  const typicalLength = lengths[Math.min(lengths.length - 1, Math.max(0, rank - 1))];
  const estimated = Math.ceil(typicalLength * fontSize * 0.55) + 10;
  return Math.min(maxWidth, Math.max(minWidth, estimated));
};

/**
 * Rig/Frac Recent Activity (EVA-76). HORIZONTAL bars: the category axis is the
 * y-axis (group values), the value axis is the x-axis (the summed count for the
 * last 7-day window). One bar per group, sorted DESCENDING by value (largest at
 * top via `yAxis.inverse`), integer value axis, palette colours per bar.
 *
 * Deliberately NOT the stacked-bars+line Activity translator: this is a single
 * horizontal bar series whose data carries per-bar colours; the legend is fed
 * separately from the raw `apiResponse.series` (one item per group).
 */
export function translateRigFracRecentActivityResponseToOptions(
  states: Partial<EvaChartStates>
) {
  const { title, subtitle, layout, series, chartType, seriesColors, request } = states;

  if (!series || series.length === 0) {
    return {};
  }

  // Each response series is one group with a single value (y[0]). Sort
  // descending so the largest group is drawn at the top of the category axis.
  // Others sits last, like the focus list, even when it is the largest group.
  const groups = series
    .map((s) => ({
      label: s.label,
      value: Number(s.y?.[0]) || 0,
      color: resolveRigFracSeriesColor(s.label, seriesColors, s.style?.hexColor ?? "#000")
    }))
    .sort(
      (a, b) =>
        Number(isOthersGroupLabel(a.label)) - Number(isOthersGroupLabel(b.label)) ||
        b.value - a.value
    );

  // Resolved like the controller does before fetching, so a chart saved with a
  // legacy metric key still gets its short label.
  const { metric } = resolveRigFracMetrics(
    request as unknown as Parameters<typeof resolveRigFracMetrics>[0]
  );
  const { groupBy } = (request as unknown as Maybe<{ groupBy?: RigFracGroupBy }>) ?? {};

  // The backend emits a VERTICAL-bar layout (category on x, value on y), so its
  // axis titles are for that orientation: layout.xAxis.title = the GROUP
  // dimension ("Operator"), layout.yAxis.title = the METRIC ("Rig Count"). This
  // translator flips to horizontal, so the titles swap axes too — otherwise the
  // value axis would read "Operator" and the category axis "Rig Count".
  const groupTitle = layout?.xAxis?.title ?? "";
  const metricTitle = layout?.yAxis?.title ?? "";

  // Size the left band to the actual group labels (see recentCategoryLabelWidth):
  // nameGap = width + 16 and grid.left = width + 32 preserve the original fixed
  // 76 / 92 / 108 ratios while adapting to short-vs-long names. The
  // RigFracMetricSelector `bottom` button reads this grid.left back at runtime to
  // stay centred on the value-axis title, so the two never drift.
  const categoryLabelWidth = recentCategoryLabelWidth(
    groups.map((g) => g.label),
    DEFAULT_AXIS_LABEL_FONT_SIZE
  );
  const categoryNameGap = categoryLabelWidth + 16;
  const gridLeft = categoryLabelWidth + 32;

  // Category (group) axis — vertical (y). `inverse` puts the first (largest)
  // group at the top instead of echarts' default bottom-up ordering. Titled by
  // the GROUP dimension; labels are truncated so the rotated title has room.
  const yAxis: Partial<YAXisOption> = {
    type: "category",
    inverse: true,
    name: groupTitle,
    nameLocation: "middle",
    nameGap: categoryNameGap,
    nameTextStyle: { fontSize: DEFAULT_AXIS_FONT_SIZE },
    data: groups.map((g) => g.label),
    // Explicit splitLine (off — a category axis defaults to no gridlines) so the
    // screenshot Y-Axis Major toggle has an object to flip rather than being a
    // no-op (EVA-76 QA).
    splitLine: { show: false },
    axisLabel: {
      fontWeight: DEFAULT_FONT_WEIGHT,
      color: AXIS_LABEL_COLOR,
      fontSize: DEFAULT_AXIS_LABEL_FONT_SIZE,
      width: categoryLabelWidth,
      overflow: "truncate"
    }
  };

  // Value (count) axis — horizontal (x), integer scale (shared with Activity).
  // Titled by the METRIC. No explicit max (wells convention): ECharts
  // nice-rounds the ceiling, and minInterval 1 keeps the all-zero case safe.
  const xAxis: Partial<XAXisOption> = {
    name: metricTitle,
    nameLocation: "middle",
    nameGap: 30,
    type: "value",
    min: 0,
    // Explicit splitLine (on — matches a value axis's default gridlines) so the
    // screenshot X-Axis Major toggle actually controls them (EVA-76 QA).
    splitLine: { show: true },
    ...integerValueAxisOptions
  };

  const barSeries = createEntityState(ChartOption.Bar, {
    id: "recent-activity",
    // Tooltip rows read seriesName; name it by the METRIC so a hovered bar reads
    // "<group> — <metric>: <value>".
    name: metricTitle || "Count",
    data: groups.map((g) => ({ value: g.value, itemStyle: { color: g.color } })),
    z: DEFAULT_Z_INDEX,
    yAxisIndex: 0,
    xAxisIndex: 0
  });

  return createEntityState(ChartOption.Chart, {
    chartType,
    toolbox: { show: false },
    // No dataZoom sliders (inherited from the wells default) — this widget has
    // its own chrome and the sliders make no sense for a ranked horizontal bar.
    dataZoom: [],
    tooltip: {
      ...rigFracTooltip,
      axisPointer: { type: "none" },
      trigger: "item",
      formatter: buildRecentActivityItemTooltipFormatter(
        getRigFracGroupByLabel(groupBy),
        rigFracMetricLabel(metric) || metricTitle
      )
    },
    // Insets are fixed at render (containLabel off) so the rotated category-axis
    // title has a reserved band — but `left` now ADAPTS to the group labels
    // (categoryLabelWidth + the "Operator"/"Basin" title) so short basin names
    // don't strand a wide empty gap and long operator names still fit. `bottom`
    // fits the value labels + the "Rig Count" title (nameGap 30). Top leaves room
    // for the title AND the "Last 7 days of Available data (…)" subtext beneath
    // it, now with extra separation (#3). The legend floats over the plot
    // (ChartLegend). NOTE: RigFracMetricSelector's `bottom` button reads this
    // grid.left back to stay centred — keep that coupling in mind if you change
    // the left/right formula.
    grid: { top: 76, left: gridLeft, right: 32, bottom: 52, containLabel: false },
    title: {
      ...createEntityState(ChartOption.Title, {
        text: title,
        // Range message below the title, e.g. "Last 7 days of Available data
        // (2026-06-26 - 2026-07-03)" — the backend supplies it for this chart so
        // the user knows exactly which window the bars cover.
        subtext: subtitle ?? undefined,
        // Extra gap between the title ("Rig Count") and the subtext so they read
        // as two distinct lines and the subtext clears the bars (#3). Overrides
        // DEFAULT_TITLE_OPTIONS.itemGap (0).
        itemGap: 12
      })
    },
    yAxis: yAxis as unknown as YAXisOption,
    xAxis: xAxis as unknown as XAXisOption,
    series: [barSeries]
  });
}
